// Seven detonation looks of mockup round 065, group A "the contained blasts"
// (R2079, `ui-map-interface.md` §6.3): card 01 FIREBALL TO THE RIM, card 02
// PRESSURE SHELL, card 03 CAVITATION COLLAPSE, card 04 SHATTERED DISC, card
// 05 SONAR RETURN, card 07 DEPTH CHARGE BOIL and card 08 HEX STAMP.
//
// Each recipe is the card's `draw(c, t, p)` line for line, through the kit
// (`detonation-recipe-kit.ts` states the translation). Every figure that
// sets the TIME or the WEIGHT of a look stands in its `fig` table of
// `data/presentation/detonation.toml`, read by name through `figs`. The
// shape of a curve stays here as a named constant or as the card's own
// ratio, the rule the combat recipes follow (R1007).
//
// The four kinds of the card are all here, and `formOf` picks one for
// each live round (R2783, R2840). The two buff forms of mockup round 131,
// `screen` and `surge`, stand in the last section, one heading a card
// (R2842).

import {
  TAU, INK, arc, band, buffEnv, circle, clamp01, curtain, dashed, disc, fade, fwd, glow2, glow3,
  glow4, hexFill, hexOutline, inCubic, inside, lerp, line, once, outCubic, outExpo, polyline, px,
  reachOf, rnd, seed, shape, smooth,
  figs, withBuffForms, type DetonationCtx, type DetonationRecipe,
} from './detonation-recipe-kit';
import {
  BOIL_SWING, BOIL_SWING_MID, BOIL_SWING_RATE, BOIL_SWING_STEP,
  boilEdge, boilNotch, boilOf, hexesOf, shardsOf, type Boil, type HexSet, type Shard,
} from './detonation-shapes';
import { boilKey, quietKey, shardKey } from './detonation-cells';

const { white: WHITE, fire: FIRE, amber: AMBER, hot: HOT, plume: PLUME, foam: FOAM, steel: STEEL, water: WATER } = INK;

// ---------------------------------------------------------------------------
// The helpers of group A
// ---------------------------------------------------------------------------

/** The weight of an alpha, the group's `W`: calm at power 0, full at
 *  power 1, and bright on a splash-0 round so its one mark reads. */
const WEIGHT_BASE = 0.38;
const WEIGHT_POWER = 0.62;
const WEIGHT_POINT = 0.8;
function W(c: DetonationCtx): number {
  return c.R > 0 ? WEIGHT_BASE + WEIGHT_POWER * c.power : WEIGHT_POINT;
}

/** Every kind but a blast is cool: it wears the firer's ink, foam and
 *  steel in place of the heat. */
function cool(c: DetonationCtx): boolean {
  return c.form !== 'blast';
}

/** A ring whose outer edge lies on `r`, never past it: the group's `ring`. */
function ringA(c: DetonationCtx, r: number, ink: number, a: number, w: number): void {
  if (a <= 0.004 || r <= 0) return;
  circle(c, 0, 0, Math.max(0.05, r - w / 2), ink, Math.min(1, a), w);
}

/** The same ring, dashed `on` and `off` px from `start`. */
function ringDashA(
  c: DetonationCtx, r: number, ink: number, a: number, w: number, on: number, off: number, start = 0,
): void {
  if (a <= 0.004 || r <= 0) return;
  dashed(c, 0, 0, Math.max(0.05, r - w / 2), ink, Math.min(1, a), w, on, off, start);
}

/** The heat ramp of `ui-map-interface.md` §6.3 by age: the group's `heat`. */
function heat(u: number): number {
  return u < 0.12 ? WHITE : u < 0.32 ? PLUME : u < 0.58 ? FIRE : u < 0.8 ? AMBER : HOT;
}

/**
 * A filled ellipse of one ink: a flat gradient cell squashed to `ry`. The
 * card's `disc` under `scale(1, sq)`. It rides the quad sink even when it
 * is round, because the card paints the bubble's glow OVER it, and the fx
 * layer flushes its quads first (`fast/layers/fx.ts`).
 */
function ellipse(c: DetonationCtx, x: number, y: number, rx: number, ry: number, ink: number, a: number): void {
  glow2(c, x, y, rx, 0, ink, a, 1, ink, a, ry);
}

/** The pieces of an ellipse the cards stroke under a squash. */
const ELLIPSE_STEPS = 48;
const exs = new Float64Array(ELLIPSE_STEPS + 1);
const eys = new Float64Array(ELLIPSE_STEPS + 1);

/** A stroked ellipse arc about `(x, y)`, radius `r` across and `r · sq`
 *  down, from `a0` to `a1`: a circle or an arc under `scale(1, sq)`. */
function ellipseArc(
  c: DetonationCtx, x: number, y: number, r: number, sq: number, a0: number, a1: number,
  ink: number, a: number, w: number,
): void {
  const n = Math.max(2, Math.ceil((ELLIPSE_STEPS * (a1 - a0)) / TAU));
  for (let i = 0; i <= n; i++) {
    const th = a0 + ((a1 - a0) * i) / n;
    exs[i] = x + Math.cos(th) * r;
    eys[i] = y + Math.sin(th) * r * sq;
  }
  polyline(c, exs, eys, n + 1, false, ink, a, w);
}

// ---------------------------------------------------------------------------
// Card 01, FIREBALL TO THE RIM
// ---------------------------------------------------------------------------

/** The share of the front time the fireball takes to grow. */
const FIREBALL_GROW_SHARE = 0.7;

/**
 * The live explosion of §6.3, rebuilt to scale. A white core flash, an
 * orange fireball that fills 0.6 R, and a shock ring in the faction ink
 * that stops on R. Bubbles rise inside R. A cool kind makes a cold foam
 * burst: slow bubbles sink and a steel band stays on the rim, sweep sends a
 * second ring, repair bubbles move in to the centre.
 */
function fireballRim(c: DetonationCtx): void {
  const F = figs(c, 'fireball-rim');
  const R = reachOf(c), pw = c.power, w8 = W(c), cold = cool(c), k = c.form, t = c.t;
  const T = (F.front_sec + F.front_per_px * R) * (cold ? F.cool_ratio - F.cool_power_ratio * pw : 1);
  // The core flash.
  const fl = fade(t, 0, F.flash_sec + F.flash_light_sec * (1 - pw));
  if (fl > 0) {
    glow3(c, 0, 0, R * (F.flash_share + F.flash_grow_share * outExpo(t / F.flash_grow_sec)),
      0, WHITE, fl * (cold ? F.flash_cold_share : 1) * w8,
      0.6, WHITE, 0.35 * fl * w8,
      1, WHITE, 0);
  }
  // The fireball, or the cold foam burst.
  const fu = outCubic(t / (T * FIREBALL_GROW_SHARE));
  const fa = fade(t, cold ? F.foam_fade_sec : F.fireball_fade_sec,
    cold ? F.foam_end_sec : F.fireball_end_sec + F.fireball_power_sec * pw);
  const fr = R * F.fireball_share * fu;
  if (fa > 0 && fr > 0) {
    if (!cold) {
      const cooled = smooth(F.cool_from_sec, F.cool_to_sec, t) > 0.5;
      glow4(c, 0, 0, fr,
        0, cooled ? FIRE : PLUME, 0.95 * fa * w8,
        0.45, cooled ? AMBER : FIRE, 0.8 * fa * w8,
        0.8, HOT, 0.5 * fa * w8,
        1, HOT, 0);
    } else {
      glow4(c, 0, 0, fr,
        0, FOAM, 0.32 * fa * w8,
        0.55, STEEL, 0.16 * fa * w8,
        0.85, c.ink, 0.2 * fa * w8,
        1, c.ink, 0);
      seed(c.seed + 11);
      const n = Math.round(F.foam_count + R * F.foam_per_px);
      for (let i = 0; i < n; i++) {
        const a = rnd() * TAU, d = fr * (0.55 + 0.35 * rnd()), s = (0.6 + rnd()) * Math.max(0.7, R / 45);
        circle(c, Math.cos(a) * d, Math.sin(a) * d, s * fu, FOAM, F.foam_alpha * fa * w8, px(c, 1));
      }
    }
  }
  // The shock ring, in the faction ink, stops on R.
  const ru = t / T, rr = R * outCubic(ru);
  const wr = Math.max(px(c, F.ring_screen_px), Math.min(R * F.ring_max_share, lerp(F.ring_light_px, F.ring_heavy_px, pw)));
  const ra = (ru < 1 ? F.ring_alpha : fade(t, T, T + F.ring_fade_sec + F.ring_light_sec * (1 - pw))) * w8;
  ringA(c, rr, c.ink, ra, wr);
  if (!cold) ringA(c, rr - wr, WHITE, ra * 0.7 * fade(t, 0, T), px(c, 1));
  if (k === 'slow') {
    ringA(c, R, STEEL, F.slow_rim_alpha * smooth(T * 0.8, T, t) * fade(t, F.slow_rim_fade_sec, F.slow_rim_end_sec),
      Math.max(px(c, 2), R * 0.04));
  }
  if (k === 'sweep') {
    const e = (t - T * 0.8) / T;
    if (e > 0 && e < 1.6) ringA(c, R * outCubic(e), c.ink, 0.45 * fade(e, 0.9, 1.6), px(c, 1.2));
  }
  // The bubbles. A bubble the card's clip would cut at the rim is left out.
  seed(c.seed + 23);
  const nb = Math.round(k === 'sweep' ? 2 + R * 0.1 : F.bubble_count + R * F.bubble_per_px);
  const bs = Math.max(0.6, Math.min(2.4, R / 25)), bw = px(c, 1);
  for (let i = 0; i < nb; i++) {
    const a = rnd() * TAU, dd = Math.sqrt(rnd()), ts = 0.06 + rnd() * 0.3;
    const life = (cold ? F.bubble_cold_life_sec : F.bubble_life_sec) + rnd() * 0.8;
    const sz = bs * (0.5 + rnd()), ph = rnd() * TAU;
    const e = t - ts;
    if (e < 0 || e > life) continue;
    const u = e / life, al = smooth(0, 0.1, u) * fade(u, 0.5, 1);
    let x: number, y: number;
    if (k === 'repair') {
      const d0 = R * (0.78 + 0.18 * dd), d = d0 * (1 - 0.75 * outCubic(u));
      x = Math.cos(a) * d; y = Math.sin(a) * d - 0.1 * R * u;
    } else if (k === 'slow') {
      const d0 = dd * 0.7 * R;
      x = Math.cos(a) * d0; y = Math.sin(a) * d0 + 0.18 * R * (1 - Math.exp(-2 * e));
    } else {
      const d0 = dd * 0.7 * R;
      x = Math.cos(a) * d0 + Math.sin(e * 5 + ph) * sz; y = Math.sin(a) * d0 - (F.bubble_rise_px_per_sec + R * 0.12) * e;
    }
    if (!inside(x, y, sz + bw / 2, R)) continue;
    circle(c, x, y, sz, FOAM, F.bubble_alpha * al * w8, bw);
  }
}

// ---------------------------------------------------------------------------
// Card 02, PRESSURE SHELL
// ---------------------------------------------------------------------------

/**
 * One thick rim ring snaps to R almost at once, then thins and fades. The
 * inside stays nearly empty. The kind sets the stroke: a blast is solid and
 * cools from white to red, slow is dashed and the dashes drag to a stop,
 * sweep is double and the inner line pings, repair is a row of beads that
 * knit into one line.
 */
function pressureShell(c: DetonationCtx): void {
  const F = figs(c, 'pressure-shell');
  const R = reachOf(c), pw = c.power, w8 = W(c), k = c.form, cold = cool(c), t = c.t;
  const Ts = F.front_sec + F.front_per_px * R;
  const Tend = k === 'blast' ? F.blast_end_sec + F.blast_light_sec * (1 - pw) : F.cool_end_sec + F.cool_light_sec * (1 - pw);
  const r = R * outExpo(t / Ts), u = smooth(Ts, Tend, t);
  const a = (1 - u) * (cold ? lerp(0.55, 1, pw) : w8), snap = fade(t, Ts, Ts * 3 + 0.05);
  const w = Math.max(px(c, 1), Math.min(R * 0.3, lerp(F.width_light_px, F.width_heavy_px, pw)) * (1 - 0.7 * u) * (1 + 0.6 * snap));
  const fl = fade(t, 0, F.flash_sec + F.flash_light_sec * (1 - pw));
  if (fl > 0) {
    glow3(c, 0, 0, Math.max(0.1, r),
      0, WHITE, fl * (0.06 + 0.55 * pw),
      0.7, cold ? STEEL : PLUME, fl * (0.03 + 0.25 * pw),
      1, cold ? c.ink : FIRE, fl * 0.12 * w8);
  }
  if (a <= 0) return;
  if (k === 'blast') {
    ringA(c, r, u < 0.06 ? WHITE : u < 0.25 ? PLUME : u < 0.55 ? FIRE : HOT, a, w);
    ringA(c, r - w * 0.5, WHITE, a * snap * 0.8, w * 0.35);
  } else if (k === 'slow') {
    const n = Math.max(6, Math.round((TAU * R) / 12)), seg = (TAU * Math.max(0.05, r - w / 2)) / n;
    const turn = F.drag_rad * (1 - Math.exp(-3 * t));
    ringDashA(c, r, c.ink, a, w, seg * 0.55, seg * 0.45, turn);
    ringDashA(c, r - w, STEEL, a * 0.5, px(c, 1), seg * 0.55, seg * 0.45, turn);
  } else if (k === 'sweep') {
    const gap = Math.max(1.6, Math.min(7, R * 0.07));
    const ping = 0.35 + 0.65 * Math.pow(Math.abs(Math.cos((t * Math.PI) / F.ping_sec)), 4);
    ringA(c, r, c.ink, a, Math.max(px(c, 1), w * 0.55));
    ringA(c, r - gap - w * 0.55, STEEL, a * ping, px(c, 1.2));
  } else {
    const n = Math.max(8, Math.round((TAU * R) / 9));
    const br = Math.max(px(c, 1.8), Math.min(R * 0.1, 1.4 + 2.2 * pw)), rb = Math.max(0.05, r - br);
    const knit = smooth(Ts + 0.1, Ts + 0.9, t), half = (TAU / n / 2) * knit;
    for (let q = 0; q < n; q++) {
      const ang = (q * TAU) / n, x = Math.cos(ang) * rb, y = Math.sin(ang) * rb;
      disc(c, x, y, br, c.ink, a);
      disc(c, x, y, br * 0.45, WHITE, a * 0.7);
      if (knit > 0) arc(c, 0, 0, rb, ang - half, ang + half, FOAM, a * 0.8, Math.max(px(c, 1), br * 0.6));
    }
  }
}

// ---------------------------------------------------------------------------
// Card 03, CAVITATION COLLAPSE
// ---------------------------------------------------------------------------

/** The arc of the bubble's highlight, in turns of a half circle. */
const GLINT_FROM = Math.PI * 1.05;
const GLINT_TO = Math.PI * 1.45;

/**
 * A dark gas bubble grows to R, falls in on itself to a point with a bright
 * flash, then a smaller second bubble grows and falls. Heavy damage gives a
 * fast, violent collapse with a white shock ring and jets; zero damage a
 * soft, slow breath. Slow sags down and does not rebound. Sweep has a
 * dashed wall and sends a ping to R. Repair carries points of light in and
 * lifts a glow.
 */
function cavitationCollapse(c: DetonationCtx): void {
  const F = figs(c, 'cavitation-collapse');
  const R = reachOf(c), pw = c.power, w8 = W(c), k = c.form, cold = cool(c), t = c.t;
  const T1 = (F.front_sec + F.front_per_px * R) * lerp(F.light_ratio, 1, pw), tc = 2 * T1, T2 = F.rebound_share * T1;
  const A2 = R * lerp(F.rebound_light_share, F.rebound_heavy_share, pw) * (k === 'slow' ? 0.35 : 1);
  let r = 0, cy = 0, sq = 1, bright = 0.5, cp = 0, al = 1;
  if (t < T1) r = R * outCubic(t / T1);
  else if (t < tc) {
    cp = (t - T1) / T1; r = R * (1 - inCubic(cp)); bright = 0.5 + 0.5 * cp;
    if (k === 'slow') { cy = 0.2 * R * cp * cp * cp; sq = 1 - 0.15 * cp; }
  } else if (t < tc + 2 * T2) {
    r = A2 * Math.sin((Math.PI * (t - tc)) / (2 * T2)); al = F.rebound_alpha;
    if (k === 'slow') cy = 0.2 * R;
  }
  const col = cold ? (k === 'slow' ? STEEL : c.ink) : (bright > 0.85 ? WHITE : PLUME);
  if (r > 0.3) {
    const lw = Math.max(px(c, 1), Math.min(r * 0.2, lerp(F.wall_light_px, F.wall_heavy_px, pw)));
    ellipse(c, 0, cy, r, r * sq, WATER, F.water_alpha * w8 * al);
    glow4(c, 0, cy, r,
      0, col, 0,
      0.7, col, 0.04 * al,
      0.7, col, 0.04 * al,
      1, col, F.wall_glow_alpha * w8 * bright * al, r * sq);
    const rw = Math.max(0.05, r - lw / 2);
    if (k === 'sweep') {
      dashed(c, 0, cy, rw, col, 0.9 * w8 * al, lw, Math.max(px(c, 2), r * 0.12), Math.max(px(c, 2), r * 0.08));
    } else if (sq === 1) {
      circle(c, 0, cy, rw, col, 0.9 * w8 * al, lw);
    } else {
      ellipseArc(c, 0, cy, rw, sq, 0, TAU, col, 0.9 * w8 * al, lw);
    }
    if (!cold) circle(c, 0, cy, Math.max(0.05, r - lw * 1.5), FIRE, 0.4 * w8 * al * bright, px(c, 1));
    if (sq === 1) arc(c, -r * 0.28, cy - r * 0.28, r * 0.45, GLINT_FROM, GLINT_TO, WHITE, 0.45 * w8 * al, px(c, 1.2));
    else ellipseArc(c, -r * 0.28, cy - r * 0.28 * sq, r * 0.45, sq, GLINT_FROM, GLINT_TO, WHITE, 0.45 * w8 * al, px(c, 1.2));
    if (k === 'repair' && t >= T1 && t < tc) {
      const n = Math.round(6 + R / 6);
      for (let q = 0; q < n; q++) {
        const a = (q * TAU) / n + cp * 0.8;
        disc(c, Math.cos(a) * r * 0.9, cy + Math.sin(a) * r * 0.9 * sq, Math.max(0.6, R * 0.025), FOAM, 0.8 * w8);
      }
    }
  }
  const fe = t - tc;
  if (fe >= 0) {
    const ccy = k === 'slow' ? 0.2 * R : 0, fl = fade(fe, 0, F.flash_sec + F.flash_light_sec * (1 - pw));
    if (fl > 0) {
      glow3(c, 0, ccy, R * (0.18 + 0.3 * pw),
        0, WHITE, fl * (0.3 + 0.7 * pw),
        0.4, cold ? FOAM : PLUME, fl * 0.5 * w8,
        1, cold ? c.ink : FIRE, 0);
    }
    if (!cold && pw > F.jet_power_share) {
      ringA(c, R * outCubic(fe / (0.08 + 0.002 * R)), WHITE, pw * fade(fe, 0, F.shock_sec), px(c, 1.5));
      const n = 5 + Math.round(R / 12), ja = fade(fe, 0, F.jet_sec);
      seed(c.seed + 31);
      for (let q = 0; q < n; q++) {
        const a = rnd() * TAU, L = R * (0.25 + 0.25 * rnd()) * outCubic(fe / 0.1);
        line(c, 0, 0, Math.cos(a) * L, Math.sin(a) * L, PLUME, ja * pw, px(c, 1.3));
      }
    }
    if (k === 'sweep') {
      const u = fe / (0.15 + 0.003 * R);
      if (u < 1.8) ringDashA(c, R * outCubic(u), STEEL, 0.6 * w8 * fade(u, 0.9, 1.8), px(c, 1), px(c, 3), px(c, 3));
    }
    if (k === 'repair') {
      const gy = -0.25 * R * outCubic(fe / 1.2);
      glow2(c, 0, gy, R * 0.35, 0, c.ink, 0.3 * fade(fe, 0.4, 1.4), 1, c.ink, 0);
    }
  }
}

// ---------------------------------------------------------------------------
// Card 04, SHATTERED DISC
// ---------------------------------------------------------------------------

/** The shards of a round and the atlas key of each. */
interface ShardSet { shards: readonly Shard[]; keys: readonly string[] }
const sxs = new Float64Array(64);
const sys = new Float64Array(64);

/** The shards of the round a context names, and their atlas keys. The
 *  weight goes in thousandths, the figure a key carries. */
function shardSetFor(c: DetonationCtx): ShardSet {
  const R = reachOf(c), pwQ = Math.round(c.power * 1000);
  const shards = shardsOf(R, pwQ, c.seed);
  return { shards, keys: shards.map((_, i) => shardKey(R, pwQ, c.seed, i)) };
}

/** Where `placeShard` put a shard: its origin, its scale and its corner
 *  count. The corners stand in `sxs`, `sys`. */
const placed = { x: 0, y: 0, s: 1, n: 0 };

/**
 * Shard `s` moved by `(mx, my)` and scaled `sc` about its centroid, with
 * the card's clip as a move: a shard that crosses R moves back in along
 * its bearing until its farthest corner stands on R, and a shard the move
 * cannot bring in shrinks toward the impact. It writes `placed`.
 */
function placeShard(s: Shard, R: number, mx: number, my: number, sc: number): void {
  const n = Math.min(s.pts.length, sxs.length);
  let ox = s.cx + mx, oy = s.cy + my, os = sc;
  for (let q = 0; q < n; q++) {
    sxs[q] = ox + (s.pts[q][0] - s.cx) * os;
    sys[q] = oy + (s.pts[q][1] - s.cy) * os;
  }
  if (s.d > 0.01) {
    const ux = s.cx / s.d, uy = s.cy / s.d;
    let shift = 0;
    for (let q = 0; q < n; q++) {
      const r2 = sxs[q] * sxs[q] + sys[q] * sys[q];
      if (r2 <= R * R) continue;
      const along = sxs[q] * ux + sys[q] * uy;
      shift = Math.max(shift, along - Math.sqrt(Math.max(0, along * along - r2 + R * R)));
    }
    if (shift > 0) {
      ox -= ux * shift; oy -= uy * shift;
      for (let q = 0; q < n; q++) { sxs[q] -= ux * shift; sys[q] -= uy * shift; }
    }
  }
  let far = 0;
  for (let q = 0; q < n; q++) far = Math.max(far, Math.hypot(sxs[q], sys[q]));
  if (far > R) {
    const k2 = R / far;
    ox *= k2; oy *= k2; os *= k2;
    for (let q = 0; q < n; q++) { sxs[q] *= k2; sys[q] *= k2; }
  }
  placed.x = ox; placed.y = oy; placed.s = os; placed.n = n;
}

/**
 * A flat disc flashes out to R. Dark cracks cut it into shards that drift
 * apart a little and fade. The outer shards keep the rim, so the edge stays
 * on R. Slow shards sink and stay longer. Sweep shards stay in place and
 * light up as two scan waves pass. Repair shards start apart and knit into
 * one disc.
 *
 * The card clips a shard that drifts past R. A baked shard cannot be cut,
 * so a shard that would cross R moves in along its bearing until its
 * farthest corner touches R, and its strokes move with it.
 */
function shatteredDisc(c: DetonationCtx): void {
  const F = figs(c, 'shattered-disc');
  const R = reachOf(c), pw = c.power, w8 = W(c), k = c.form, cold = cool(c), t = c.t;
  const Tf = F.front_sec + F.front_per_px * R;
  const Tc = Tf + F.crack_sec + F.crack_light_sec * (1 - pw);
  const life = k === 'blast' ? F.blast_life_sec + F.blast_light_sec * (1 - pw) : k === 'sweep' ? F.sweep_life_sec : F.cool_life_sec;
  const fd = w8 * (cold ? F.cold_flash_alpha : F.flash_alpha) * fade(t, Tc, Tc + 0.12);
  if (fd > 0) {
    glow3(c, 0, 0, R * outExpo(t / Tf),
      0, WHITE, fd,
      0.7, cold ? FOAM : PLUME, fd * 0.8,
      1, cold ? c.ink : FIRE, fd * 0.7);
  }
  if (t < Tc * 0.6) return;
  const set = once(4, c, shardSetFor);
  const crack = fade(t, Tc, Tc + 0.2), gone = 1 - smooth(Tc + 0.2, life, t);
  let sep = outCubic((t - Tc) / (life - Tc));
  if (k === 'repair') sep = 1 - outCubic((t - Tc) / (life * 0.45));
  const lw = px(c, k === 'blast' ? 1 + pw * 0.6 : 1);
  for (let i = 0; i < set.shards.length; i++) {
    const s = set.shards[i];
    let sc = 1 - 0.3 * sep, mx = 0, my = 0, a = gone * w8;
    if (k === 'slow') { my = 0.08 * R * sep * (0.6 + 0.4 * s.r1); sc = 1 - 0.15 * sep; }
    else if (k === 'sweep') {
      sc = 0.9;
      let b = 0;
      for (let q = 0; q < 2; q++) {
        const rw = (R * (t - Tc - q * 0.7)) / 0.6;
        b = Math.max(b, Math.exp(-Math.pow((s.d - rw) / (0.18 * R + 2), 2)));
      }
      a *= 0.25 + 0.75 * b;
    } else if (s.d > 0.01) {
      const m = (0.1 * R * sep * (0.5 + s.r1)) / R;
      mx = (s.cx * m * R) / Math.max(s.d, 1); my = (s.cy * m * R) / Math.max(s.d, 1);
    }
    if (k === 'repair') a *= 0.5 + 0.5 * (1 - sep);
    placeShard(s, R, mx, my, sc);
    const n = placed.n, ox = placed.x, oy = placed.y, os = placed.s;
    if (cold) {
      shape(c, set.keys[i], ox, oy, os, c.ink, 0.16 * a);
      polyline(c, sxs, sys, n, true, k === 'repair' ? FOAM : STEEL, 0.7 * a, lw);
    } else {
      shape(c, set.keys[i], ox, oy, os, heat(0.28 + 0.5 * sep + 0.25 * s.r1), 0.7 * a);
      polyline(c, sxs, sys, n, true, PLUME, Math.max(0.6 * a, crack * w8), lw);
    }
    if (crack > 0 && t >= Tc) polyline(c, sxs, sys, n, true, WATER, 0.8 * crack, px(c, 1.4));
  }
}

// ---------------------------------------------------------------------------
// Card 05, SONAR RETURN
// ---------------------------------------------------------------------------

/** One ring of returns: its radius and its arcs as [start, length]. */
interface Returns { r: number; segs: readonly (readonly [number, number])[] }

/** The returns of card 05: the card's `returns05`. */
function returnsOf(R: number, pw: number, k: DetonationCtx['form'], s0: number): Returns[] {
  seed(s0 + 505);
  const sp = k === 'blast' ? lerp(7, 3.5, pw) : k === 'slow' ? 14 : k === 'sweep' ? 9 : 11;
  const m = Math.max(1, Math.round(R / sp)), rings: Returns[] = [];
  for (let j = 1; j < m; j++) {
    const r = (R * j) / m, segs: [number, number][] = [];
    if (k === 'blast') {
      let a = rnd() * TAU;
      const end = a + TAU;
      while (a < end) {
        const len = (2 + rnd() * 6) / r;
        if (rnd() < 0.62) segs.push([a, Math.min(len, end - a)]);
        a += len + (1 + rnd() * 3) / r;
      }
    } else if (k === 'slow') {
      const n = 3 + Math.floor(rnd() * 3), off = rnd() * TAU;
      for (let q = 0; q < n; q++) segs.push([off + (q * TAU) / n, (TAU / n) * (0.55 + rnd() * 0.25)]);
    } else if (k === 'sweep') {
      segs.push([0, TAU]);
    } else {
      const n = 4 + (j % 2) * 2, off = rnd() * TAU;
      for (let q = 0; q < n; q++) segs.push([off + (q * TAU) / n, (TAU / n) * 0.7]);
    }
    rings.push({ r, segs });
  }
  return rings;
}

/** The returns of the round a context names. */
function returnsFor(c: DetonationCtx): Returns[] {
  return returnsOf(reachOf(c), c.power, c.form, c.seed);
}

/** A stroked arc with the round caps the card's stroke inherits: a half
 *  disc of half the width at each end, turned out along the arc. */
function cappedArc(c: DetonationCtx, r: number, a0: number, len: number, ink: number, a: number, w: number): void {
  arc(c, 0, 0, r, a0, a0 + len, ink, a, w);
  const a1 = a0 + len;
  arc(c, Math.cos(a0) * r, Math.sin(a0) * r, w / 4, a0 - Math.PI, a0, ink, a, w / 2);
  arc(c, Math.cos(a1) * r, Math.sin(a1) * r, w / 4, a1, a1 + Math.PI, ink, a, w / 2);
}

/**
 * The blast as an instrument sees it. A thin scan line runs out at an even
 * speed. Thin arc returns light up behind it, the rim ping lights bright on
 * R, and range ticks mark the rim. A blast gives dense, broken returns in
 * heat colours and 48 short ticks; slow long arcs that settle inward and 12
 * clamp ticks; sweep dotted rings and a ruler; repair short arcs and eight
 * chevrons that point in.
 */
function sonarReturn(c: DetonationCtx): void {
  const F = figs(c, 'sonar-return');
  const R = reachOf(c), pw = c.power, w8 = W(c), k = c.form, cold = cool(c), t = c.t;
  const T = F.front_sec + F.front_per_px * R;
  const end = k === 'blast' ? F.blast_end_sec + F.blast_light_sec * (1 - pw) : F.cool_end_sec;
  const go = fade(t, end - 0.5, end);
  if (go <= 0) return;
  const rf = R * clamp01(t / T);
  // The returns.
  const rings = once(5, c, returnsFor);
  const hold = k === 'blast' ? F.blast_hold_sec : F.cool_hold_sec;
  const rw = px(c, k === 'blast' ? 1 + 0.5 * pw : 1);
  for (let j = 0; j < rings.length; j++) {
    const g = rings[j], tj = (T * g.r) / R;
    if (t < tj) continue;
    const e = t - tj, a = Math.min(1, w8 * go * (0.3 + 0.7 * Math.exp(-e / hold)));
    const r = k === 'slow' ? g.r * (1 - 0.05 * smooth(0, 2, e)) : g.r;
    const col = k === 'blast' ? heat(0.2 + (0.7 * g.r) / R) : j % 2 ? STEEL : c.ink;
    if (k === 'sweep') { dashed(c, 0, 0, r, col, a, rw, px(c, 1.2), px(c, 3)); continue; }
    for (const s of g.segs) {
      if (k === 'blast') cappedArc(c, r, s[0], s[1], col, a, rw);
      else arc(c, 0, 0, r, s[0], s[0] + s[1], col, a, rw);
    }
  }
  // The scan line.
  if (t < T) {
    ringA(c, rf, cold ? STEEL : WHITE, 0.9 * w8, px(c, 1.3));
    ringA(c, rf, cold ? c.ink : FIRE, 0.18 * w8, Math.min(rf, px(c, 6)));
  }
  // The rim ping and the range ticks.
  if (t >= T) {
    const e = t - T, pa = w8 * go * (0.35 + 0.65 * Math.exp(-e / 0.25));
    ringA(c, R, cold ? c.ink : PLUME, pa, px(c, 2));
    ringA(c, R - px(c, 2), cold ? STEEL : WHITE, pa * 0.6 * Math.exp(-e / 0.15), px(c, 1));
    const ta = w8 * go * smooth(0, 0.15, e) * 0.8, tk = Math.max(1.5, Math.min(6, R * 0.08));
    const ink = cold ? STEEL : AMBER, w = px(c, 1);
    if (k === 'blast') {
      for (let q = 0; q < 48; q++) {
        const a = (q * TAU) / 48, L = q % 6 ? tk * 0.6 : tk * 1.4, ca = Math.cos(a), sa = Math.sin(a);
        line(c, ca * (R - L), sa * (R - L), ca * R, sa * R, ink, ta, w, R);
      }
    } else if (k === 'slow') {
      for (let q = 0; q < 12; q++) {
        const a = (q * TAU) / 12, L = Math.max(3, R * 0.12), b = Math.max(1.2, R * 0.025), xr = R - L;
        const x0 = Math.cos(a), y0 = Math.sin(a);
        line(c, x0 * xr, y0 * xr, x0 * R, y0 * R, ink, ta, w, R);
        line(c, x0 * xr - y0 * b, y0 * xr + x0 * b, x0 * xr + y0 * b, y0 * xr - x0 * b, ink, ta, w, R);
      }
    } else if (k === 'sweep') {
      for (let q = 0; q < 36; q++) {
        const a = (q * TAU) / 36, L = q % 3 ? tk * 0.5 : tk * 1.5, ca = Math.cos(a), sa = Math.sin(a);
        line(c, ca * (R - L), sa * (R - L), ca * R, sa * R, ink, ta, w, R);
      }
    } else {
      for (let q = 0; q < 8; q++) {
        const a = (q * TAU) / 8 + TAU / 16, L = tk * 1.4, b = tk * 0.7, x0 = Math.cos(a), y0 = Math.sin(a);
        const rt = R - px(c, 2) - L;
        sxs[0] = x0 * (rt + L) - y0 * b; sys[0] = y0 * (rt + L) + x0 * b;
        sxs[1] = x0 * rt; sys[1] = y0 * rt;
        sxs[2] = x0 * (rt + L) + y0 * b; sys[2] = y0 * (rt + L) - x0 * b;
        polyline(c, sxs, sys, 3, false, ink, ta, w, R);
      }
    }
  }
  // The origin mark.
  const cm = Math.min(4, R * 0.3), ca = 0.6 * w8 * go;
  line(c, -cm, 0, cm, 0, cold ? STEEL : WHITE, ca, px(c, 1));
  line(c, 0, -cm, 0, cm, cold ? STEEL : WHITE, ca, px(c, 1));
}

// ---------------------------------------------------------------------------
// Card 07, DEPTH CHARGE BOIL
// ---------------------------------------------------------------------------

/** The boil of a round and the atlas key of its baked fill. */
interface BoilSet { boil: Boil; key: string }
const bxs = new Float64Array(256);
const bys = new Float64Array(256);

/** The boil of the round a context names, and the key of its fill. */
function boilSetFor(c: DetonationCtx): BoilSet {
  const R = reachOf(c);
  return { boil: boilOf(R, c.seed), key: boilKey(R, c.seed) };
}

/**
 * The card's `ragged07` stroked: the boil's edge at scale `s`, each notch
 * swung by its own wave of `tt`. The fill is the baked boil at the middle
 * of the swing (`detonation-cells.ts`), scaled the same.
 */
function raggedStroke(c: DetonationCtx, b: Boil, R: number, s: number, tt: number, ink: number, a: number, w: number): void {
  const n = Math.min(b.M, bxs.length);
  for (let k = 0; k < n; k++) {
    const th = (k / b.M) * TAU;
    const r = boilNotch(b, R, k, s, BOIL_SWING_MID + BOIL_SWING * Math.sin(tt * BOIL_SWING_RATE + k * BOIL_SWING_STEP));
    bxs[k] = Math.cos(th) * r; bys[k] = Math.sin(th) * r;
  }
  polyline(c, bxs, bys, n, true, ink, a, w, R);
}

/**
 * The charge seen from above. A white foam boil pushes up, and its ragged
 * edge lands on R: the notches bite in, the crests touch the rim. The boil
 * breaks into foam flecks that settle and fade. Heavy damage adds a dark
 * slick at the centre and spray inside the rim. Slow foam thickens into a
 * scum and the flecks sink. Sweep comes as three hollow foam rings. Repair
 * flecks drift in and rise.
 */
function depthChargeBoil(c: DetonationCtx): void {
  const F = figs(c, 'depth-charge-boil');
  const R = reachOf(c), pw = c.power, w8 = W(c), k = c.form, cold = cool(c), t = c.t;
  const T = F.front_sec + F.front_per_px * R;
  const set = once(7, c, boilSetFor);
  const e = set.boil;
  const s = outCubic(t / T);
  const tb = k === 'blast' ? F.blast_boil_sec + F.blast_power_sec * pw : k === 'slow' ? F.slow_boil_sec : F.cool_boil_sec;
  if (!cold) {
    const fl = fade(t, 0, F.flash_sec);
    if (fl > 0) glow3(c, 0, 0, R * 0.55, 0, WHITE, fl * w8, 0.5, PLUME, fl * 0.7 * w8, 1, FIRE, 0);
  }
  // The boil body.
  if (k === 'sweep') {
    for (let i = 0; i < 3; i++) {
      const st0 = i * 0.22, si = outCubic((t - st0) / T);
      const ai = w8 * smooth(st0, st0 + 0.03, t) * fade(t, st0 + T * 0.6, st0 + T + 0.5);
      if (ai <= 0 || si <= 0) continue;
      shape(c, set.key, 0, 0, si, FOAM, 0.05 * ai);
      raggedStroke(c, e, R, si, t, i ? c.ink : FOAM, 0.75 * ai, px(c, 1.4 - i * 0.2));
    }
  } else {
    const ba = w8 * (cold ? F.cold_body_alpha : F.body_alpha) * fade(t, F.body_fade_sec, tb + 0.3);
    if (ba > 0 && s > 0) {
      shape(c, set.key, 0, 0, s, FOAM, (cold ? 0.3 : 0.75) * ba);
      raggedStroke(c, e, R, s, t, cold ? c.ink : WHITE, 0.9 * ba, px(c, 1.5));
      shape(c, set.key, 0, 0, s * 0.72, WHITE, (cold ? 0.25 : 0.6) * ba);
      if (k === 'slow') raggedStroke(c, e, R, s * 0.9, t * 0.5 + 4, STEEL, 0.6 * ba, px(c, 1));
    }
    if (pw > F.slick_power_share) {
      const dk = ((pw - F.slick_power_share) / (1 - F.slick_power_share)) * fade(t, F.slick_fade_sec, F.slick_end_sec);
      disc(c, 0, 0, R * 0.3 * s, WATER, 0.85 * dk);
      for (const q of e.spray) {
        const ts = 0.04 + q.ts * 0.2, u = (t - ts) / 0.5;
        if (u < 0 || u > 1) continue;
        const d = R * Math.min(0.97, q.d + 0.05 * u) - 0.6, sz = 0.4 + 0.6 * q.s;
        if (d + sz > R) continue;
        disc(c, Math.cos(q.a) * d, Math.sin(q.a) * d, sz, WHITE, 0.9 * dk * (1 - u));
      }
    }
  }
  // The flecks. A fleck the card's clip would cut at the rim is left out.
  const t0 = k === 'sweep' ? 0.5 : 0.25, life = k === 'blast' ? F.blast_fleck_sec : F.cool_fleck_sec;
  const nf = k === 'sweep' ? Math.round(e.flecks.length * 0.4) : e.flecks.length;
  const fs = Math.max(0.5, Math.min(1.4, R / 60));
  for (let i = 0; i < nf; i++) {
    const f = e.flecks[i], ts = t0 + f.ts * 0.3, u = (t - ts) / life;
    if (u < 0 || u > 1) continue;
    let d = f.d * boilEdge(e, R, f.a) * 0.95, x: number, y: number;
    if (k === 'repair') {
      d *= 1 - 0.6 * outCubic(u);
      x = Math.cos(f.a) * d; y = Math.sin(f.a) * d - 0.12 * R * u;
    } else if (k === 'slow') {
      x = Math.cos(f.a) * d; y = Math.sin(f.a) * d + 0.06 * R * outCubic(u);
    } else {
      d = Math.min(d * (1 + 0.04 * u), boilEdge(e, R, f.a) - 1);
      x = Math.cos(f.a) * d + Math.sin(t * 2 + f.ph) * 0.4; y = Math.sin(f.a) * d;
    }
    const al = smooth(0, 0.08, u) * fade(u, 0.4, 1) * w8 * (cold ? 0.65 : 0.9);
    const r = fs * (0.5 + f.s) * (1 - 0.4 * u);
    if (!inside(x, y, r, R)) continue;
    disc(c, x, y, r, k === 'repair' ? c.ink : FOAM, al);
  }
}

// ---------------------------------------------------------------------------
// Card 08, HEX STAMP
// ---------------------------------------------------------------------------

/** A hex is drawn at this share of the lattice radius, so a gap stands
 *  between two hexes. */
const HEX_INSET = 0.84;

/** The hex lattice of the round a context names. */
function hexesFor(c: DetonationCtx): HexSet {
  return hexesOf(reachOf(c), c.seed);
}

/**
 * Small hexes in the hex form of the map fill R. Each hex flashes on later
 * with distance, so a wave runs out. No hex crosses R: the edge is a jagged
 * hex line on R. A blast fills each hex with heat. Slow draws outlines that
 * tighten and stay. Sweep lights a dot in each hex as the wave passes.
 * Repair draws a cross in each hex, and they fade from the rim in.
 */
function hexStamp(c: DetonationCtx): void {
  const F = figs(c, 'hex-stamp');
  const R = reachOf(c), pw = c.power, w8 = W(c), k = c.form, t = c.t;
  const T = F.front_sec + F.front_per_px * R;
  const H = once(8, c, hexesFor), hs = H.h * HEX_INSET;
  const hold = k === 'blast' ? F.blast_hold_sec + F.blast_light_sec * (1 - pw)
    : k === 'slow' ? F.slow_hold_sec : k === 'sweep' ? F.sweep_hold_sec : F.repair_hold_sec;
  for (let i = 0; i < H.list.length; i++) {
    const h = H.list[i], td = (T * h.d) / R + h.j * 0.025, e = t - td;
    if (e < 0) continue;
    const flash = fade(e, 0, F.flash_sec);
    let a: number;
    if (k === 'repair') {
      const out = 0.6 + 1.2 * (1 - h.d / Math.max(1, H.dmax));
      a = w8 * fade(t, out + 0.8, out + 1.4);
    } else a = w8 * fade(e, hold, hold + 0.5);
    if (a <= 0.004) continue;
    if (k === 'blast') {
      hexFill(c, h.x, h.y, hs, flash > 0.3 ? WHITE : heat(0.3 + 0.6 * smooth(0, hold, e) + 0.1 * h.j), a * (0.35 + 0.5 * flash + 0.25 * pw));
      if (flash > 0) hexOutline(c, h.x, h.y, hs, PLUME, a * flash, px(c, 1));
    } else if (k === 'slow') {
      const r = hs * (1 - 0.15 * smooth(0, 1.2, e));
      hexOutline(c, h.x, h.y, r, flash > 0.3 ? WHITE : STEEL, a * (0.55 + 0.45 * flash), px(c, 1));
      if (flash > 0) hexFill(c, h.x, h.y, r, c.ink, 0.3 * flash * a);
    } else if (k === 'sweep') {
      const lit = fade(e, 0, 0.25);
      if (lit > 0) hexFill(c, h.x, h.y, hs, c.ink, 0.18 * lit * a);
      disc(c, h.x, h.y, Math.max(0.5, hs * (0.22 + 0.2 * lit)), lit > 0.4 ? WHITE : c.ink, a * (0.4 + 0.6 * lit));
    } else {
      hexFill(c, h.x, h.y, hs, c.ink, a * (0.06 + 0.25 * flash));
      const m = hs * 0.5, ink = flash > 0.3 ? WHITE : FOAM;
      line(c, h.x - m, h.y, h.x + m, h.y, ink, a * 0.85, px(c, 1.2));
      line(c, h.x, h.y - m, h.x, h.y + m, ink, a * 0.85, px(c, 1.2));
    }
  }
}

// ---------------------------------------------------------------------------
// The buff forms of mockup round 131 (R2842)
// ---------------------------------------------------------------------------
//
// Each pair below is the `buff.screen` and the `buff.surge` of one card of
// `archive/mockups/131-torpedo-buff-forms.html`, line for line through the
// kit. A screen (the Sepia) settles calm over the own hulls; a surge (the
// Sailfish) pushes out, harder on the forward side (`fwd`).

/** A bigger scratch for the long rim paths of the buff forms: the saw of
 *  card 02 and the noise trace of card 05. */
const RIM_STEPS = 512;
const rxs = new Float64Array(RIM_STEPS);
const rys = new Float64Array(RIM_STEPS);

// --- card 01 ------------------------------------------------------------------

/** The veil calms by this share once it reaches R. */
const VEIL_CALM_SHARE = 0.4;

/**
 * No shock ring. A soft foam veil grows to R and calms. A curtain of fine
 * bubbles stands on the rim, and the bubbles inside slow to a stop and go
 * soft.
 */
function fireballRimScreen(c: DetonationCtx): void {
  const F = figs(c, 'fireball-rim');
  const R = reachOf(c), w8 = W(c), t = c.t, T = F.screen_front_sec;
  const e = buffEnv(t, F.screen_in_sec, F.screen_out_sec, F.screen_end_sec);
  if (e <= 0) return;
  const fl = fade(t, 0, F.screen_flash_sec);
  if (fl > 0) glow2(c, 0, 0, R * 0.4, 0, FOAM, F.screen_flash_alpha * fl * w8, 1, FOAM, 0);
  // The foam veil grows to R and calms.
  const r = R * outCubic(t / T), calm = 1 - VEIL_CALM_SHARE * smooth(T, T + F.screen_calm_sec, t);
  glow4(c, 0, 0, r,
    0, FOAM, F.screen_veil_core_alpha * e,
    0.65, c.ink, F.screen_veil_ink_alpha * e * calm,
    0.92, FOAM, F.screen_veil_hem_alpha * e * calm,
    1, FOAM, 0);
  // The curtain stands on the rim once the veil reaches it.
  curtain(c, R, t, c.seed + 101, F.screen_curtain_alpha * w8 * e * smooth(T * 0.7, T + 0.3, t),
    Math.max(0.6, Math.min(1.4, R / 40)));
  // The bubbles inside slow to a stop and go soft.
  seed(c.seed + 23);
  const nb = Math.round(F.screen_bubble_count + R * F.screen_bubble_per_px);
  const bs = Math.max(0.6, Math.min(2.4, R / 25)), bw = px(c, 1);
  for (let i = 0; i < nb; i++) {
    const a = rnd() * TAU, dd = Math.sqrt(rnd()) * 0.75 * R, ts = 0.1 + rnd() * 0.5, sz = bs * (0.6 + rnd()), age = t - ts;
    if (age < 0) continue;
    const drift = 3 * (1 - Math.exp(-age * 2));
    const x = Math.cos(a) * dd, y = Math.sin(a) * dd - drift, r2 = sz * (1 + 0.5 * smooth(0, 2, age));
    if (!inside(x, y, r2 + bw / 2, R)) continue;
    circle(c, x, y, r2, FOAM,
      F.screen_bubble_alpha * w8 * e * smooth(0, 0.3, age) * (1 - 0.5 * smooth(0.5, 2.5, age)), bw);
  }
}

/**
 * A thin shock ring in the faction ink runs to R. Two pulses of foam
 * bubbles then stream out on rays, longer and faster on the forward side,
 * and go out on the rim.
 */
function fireballRimSurge(c: DetonationCtx): void {
  const F = figs(c, 'fireball-rim');
  const R = reachOf(c), w8 = W(c), t = c.t, T = F.surge_front_sec;
  const e = buffEnv(t, F.surge_in_sec, F.surge_out_sec, F.surge_end_sec);
  if (e <= 0) return;
  const fl = fade(t, 0, F.surge_flash_sec);
  if (fl > 0) glow2(c, 0, 0, R * 0.35, 0, WHITE, F.surge_flash_alpha * fl * w8, 1, FOAM, 0);
  // The thin shock ring.
  const ru = t / T;
  ringA(c, R * outCubic(ru), c.ink, (ru < 1 ? F.surge_ring_alpha : fade(t, T, T + F.surge_ring_fade_sec)) * w8,
    px(c, F.surge_ring_screen_px));
  // The push: two pulses of foam bubbles stream out on rays and go out on the rim.
  seed(c.seed + 131);
  const n = Math.round(F.surge_ray_count + R * F.surge_ray_per_px);
  const bs = Math.max(0.6, Math.min(1.6, R / 35)), bw = px(c, 1);
  for (let q = 0; q < 2; q++) {
    for (let i = 0; i < n; i++) {
      const a = ((i + rnd() * 0.6) / n) * TAU, f = fwd(c, a), ts = q * F.surge_pulse_sec + rnd() * 0.12;
      const u = (t - ts) / lerp(F.surge_slow_ray_sec, F.surge_fast_ray_sec, f);
      if (u < 0 || u > 1) continue;
      const head = R * (0.15 + 0.85 * outCubic(u)) - bs - 0.5, tail = Math.max(0, head - R * (0.12 + 0.25 * f));
      const al = w8 * e * (q ? F.surge_echo_share : 1) * (1 - smooth(0.7, 1, u));
      const ca = Math.cos(a), sa = Math.sin(a);
      line(c, ca * tail, sa * tail, ca * head, sa * head, FOAM, F.surge_streak_alpha * al, bw, R);
      if (inside(ca * head, sa * head, bs + bw / 2, R)) {
        circle(c, ca * head, sa * head, bs, FOAM, F.surge_bubble_alpha * al, bw);
      }
    }
  }
}

// --- card 02 ------------------------------------------------------------------

/**
 * The rim is one soft hem with no hard line: a band that fades inward from
 * R, widens as it settles, and holds over a faint fill.
 */
function pressureShellScreen(c: DetonationCtx): void {
  const F = figs(c, 'pressure-shell');
  const R = reachOf(c), w8 = W(c), t = c.t;
  const e = buffEnv(t, F.screen_in_sec, F.screen_out_sec, F.screen_end_sec);
  if (e <= 0) return;
  const r = R * outCubic(t / F.screen_front_sec);
  const wb = Math.min(R * 0.45, lerp(F.screen_hem_px, F.screen_hem_wide_px,
    smooth(F.screen_widen_from_sec, F.screen_widen_to_sec, t)));
  // The faint fill lies under the hem, so it rides the quad sink too.
  ellipse(c, 0, 0, r, r, FOAM, F.screen_fill_alpha * e);
  band(c, r - wb, r, FOAM, 0, F.screen_hem_alpha * w8 * e);
  band(c, r - wb * 0.4, r, c.ink, 0, F.screen_ink_alpha * w8 * e);
}

/** One saw tooth is this deep, plus as much again on the forward side,
 *  times the thrust. */
const SAW_DEPTH_PX = 2.5;
const SAW_FORWARD_PX = 4.5;
/** The saw takes one tooth each this many px of the rim. */
const SAW_TOOTH_PX = 10;

/** The saw of card 02: `n` teeth on a ring of radius `r`, each pointing
 *  out, deeper on the forward side. */
function saw(c: DetonationCtx, n: number, r: number, depthK: number, ink: number, a: number, w: number, R: number): void {
  if (a <= 0.004 || r <= 1) return;
  const m = Math.min(n * 2, RIM_STEPS - 1);
  for (let q = 0; q <= m; q++) {
    const an = (q / m) * TAU, depth = Math.min(r * 0.4, (SAW_DEPTH_PX + SAW_FORWARD_PX * fwd(c, an)) * depthK);
    const rr = q % 2 === 0 ? r - px(c, 0.8) : Math.max(0.05, r - depth);
    rxs[q] = Math.cos(an) * rr; rys[q] = Math.sin(an) * rr;
  }
  polyline(c, rxs, rys, m + 1, false, ink, a, w, R);
}

/**
 * The rim snaps to R as a saw of teeth that point out. The forward teeth
 * are deeper, and a second, faint saw runs out from the middle and joins
 * it.
 */
function pressureShellSurge(c: DetonationCtx): void {
  const F = figs(c, 'pressure-shell');
  const R = reachOf(c), w8 = W(c), t = c.t, Ts = F.front_sec + F.front_per_px * R;
  const e = buffEnv(t, F.surge_in_sec, F.surge_out_sec, F.surge_end_sec);
  if (e <= 0) return;
  const n = Math.max(10, Math.round((TAU * R) / SAW_TOOTH_PX));
  const r = R * outExpo(t / Ts), thrust = smooth(Ts, Ts + F.surge_thrust_sec, t);
  saw(c, n, r, 0.6 + 0.4 * thrust, c.ink, F.surge_saw_alpha * w8 * e, px(c, 1.4), R);
  // The second saw: a faint pulse that runs out from 0.5 R and joins the rim.
  const u = (t - F.surge_echo_sec) / F.surge_echo_run_sec;
  if (u > 0 && u < 1) {
    saw(c, n, R * (0.5 + 0.5 * outCubic(u)), 0.7, FOAM, F.surge_echo_alpha * w8 * e * (1 - smooth(0.6, 1, u)), px(c, 1), R);
  }
}

// --- card 03 ------------------------------------------------------------------

/** The wall wobbles by this share of R once it lands, at this rate, and
 *  calms at this rate a second. */
const WOBBLE_SHARE = 0.06;
const WOBBLE_RATE = 5;
const WOBBLE_CALM = 2.2;
/** The second sheen of the dome, in turns of a half circle. */
const SHEEN_FROM = Math.PI * 0.1;
const SHEEN_TO = Math.PI * 0.35;

/**
 * The gas bubble grows to R and does not fall in. Its wall wobbles, calms
 * and thins, and the bubble stays as a soft dome with a sheen over the
 * hulls.
 */
function cavitationCollapseScreen(c: DetonationCtx): void {
  const F = figs(c, 'cavitation-collapse');
  const R = reachOf(c), w8 = W(c), t = c.t;
  const T = (F.front_sec + F.front_per_px * R) * lerp(F.light_ratio, 1, c.power) * F.screen_front_ratio;
  const e = buffEnv(t, F.screen_in_sec, F.screen_out_sec, F.screen_end_sec);
  if (e <= 0) return;
  let r = R * outCubic(t / T);
  // A wobble of the wall that calms.
  if (t > T) r = R * (1 - WOBBLE_SHARE * Math.abs(Math.sin((t - T) * WOBBLE_RATE)) * Math.exp(-(t - T) * WOBBLE_CALM));
  if (r <= 0.3) return;
  const thin = 1 - 0.5 * smooth(T, T + F.screen_thin_sec, t), lw = Math.max(px(c, 1), Math.min(r * 0.15, 1.3)) * thin;
  glow3(c, 0, 0, r, 0, FOAM, 0.05 * e, 0.75, c.ink, 0.07 * e, 1, FOAM, F.screen_wall_glow_alpha * w8 * e);
  circle(c, 0, 0, Math.max(0.05, r - lw / 2), FOAM, F.screen_wall_alpha * w8 * e * thin, lw);
  // The sheen: the highlight of a dome that stays.
  arc(c, -r * 0.28, -r * 0.28, r * 0.5, GLINT_FROM, GLINT_TO, WHITE, F.screen_glint_alpha * w8 * e, px(c, 1.2));
  arc(c, r * 0.2, r * 0.2, r * 0.62, SHEEN_FROM, SHEEN_TO, FOAM, F.screen_sheen_alpha * w8 * e, px(c, 1));
}

/** The small bubble of the surge grows to this share of R. */
const SURGE_BUBBLE_SHARE = 0.45;

/**
 * A small bubble grows and falls in at once. The rebound fires tapered jets
 * out on rays, the forward jets longest, and a weaker second rebound
 * follows.
 */
function cavitationCollapseSurge(c: DetonationCtx): void {
  const F = figs(c, 'cavitation-collapse');
  const R = reachOf(c), w8 = W(c), t = c.t;
  const T1 = F.surge_bubble_sec, tc = F.surge_collapse_sec, Tj = F.surge_jet_sec;
  const e = buffEnv(t, F.surge_in_sec, F.surge_out_sec, F.surge_end_sec);
  if (e <= 0) return;
  // A small bubble grows and falls in fast.
  const r = t < T1 ? SURGE_BUBBLE_SHARE * R * outCubic(t / T1)
    : t < tc ? SURGE_BUBBLE_SHARE * R * (1 - inCubic((t - T1) / (tc - T1))) : 0;
  if (r > 0.3) {
    glow3(c, 0, 0, r, 0, c.ink, 0, 0.7, c.ink, 0.05, 1, c.ink, F.surge_wall_glow_alpha * w8);
    circle(c, 0, 0, Math.max(0.05, r - px(c, 0.6)), FOAM, F.surge_wall_alpha * w8, px(c, 1.2));
  }
  const fe = t - tc;
  if (fe < 0) return;
  const fl = fade(fe, 0, F.surge_flash_sec);
  if (fl > 0) glow2(c, 0, 0, R * 0.25, 0, WHITE, F.surge_flash_alpha * fl, 1, FOAM, 0);
  // The rebound jets run out on rays; after the head meets R the tail
  // follows it into the rim.
  const n = 6 + Math.round(R / 8);
  seed(c.seed + 303);
  for (let w = 0; w < 2; w++) {
    for (let q = 0; q < n; q++) {
      const a = ((q + rnd() * 0.5 + w * 0.5) / n) * TAU, f = fwd(c, a);
      const u = (fe - w * F.surge_rebound_sec) / (Tj * lerp(1.5, 0.8, f));
      if (u < 0 || u > 2) continue;
      const head = Math.min(R - px(c, 0.8), R * outCubic(u)), len = R * (0.25 + 0.35 * f) * (1 - smooth(1, 2, u));
      const tail = Math.max(0, head - len), al = w8 * e * (w ? F.surge_echo_share : 1) * (1 - smooth(1.3, 2, u));
      if (head - tail < 0.3) continue;
      const ca = Math.cos(a), sa = Math.sin(a);
      line(c, ca * tail, sa * tail, ca * head, sa * head, c.ink, F.surge_jet_glow_alpha * al, px(c, 2.6), R);
      line(c, ca * tail, sa * tail, ca * head, sa * head, FOAM, F.surge_jet_alpha * al, px(c, 1), R);
    }
  }
}

// --- card 04 ------------------------------------------------------------------

/** A settling shard comes in this much large and lands this much small. */
const SETTLE_FROM_SCALE = 1.18;
const SETTLE_TO_SCALE = 0.93;

/**
 * No flash and no crack. The shards come in one by one at random, as soft
 * scales with no outline, and settle from a little large to their place.
 * They hold as a matte mosaic and fade.
 */
function shatteredDiscScreen(c: DetonationCtx): void {
  const F = figs(c, 'shattered-disc');
  const R = reachOf(c), w8 = W(c), t = c.t;
  const e = buffEnv(t, F.screen_in_sec, F.screen_out_sec, F.screen_end_sec);
  if (e <= 0) return;
  const set = once(4, c, shardSetFor);
  const rf = R * outCubic(t / F.screen_front_sec);
  ellipse(c, 0, 0, rf, rf, FOAM, F.screen_fill_alpha * e);
  for (let i = 0; i < set.shards.length; i++) {
    const s = set.shards[i], ts = F.screen_settle_from_sec + s.r1 * F.screen_settle_spread_sec;
    const u = smooth(ts, ts + F.screen_settle_sec, t);
    if (u <= 0) continue;
    placeShard(s, R, 0, 0, lerp(SETTLE_FROM_SCALE, SETTLE_TO_SCALE, u));
    shape(c, set.keys[i], placed.x, placed.y, placed.s, s.r2 > 0.5 ? FOAM : c.ink,
      (F.screen_shard_alpha + F.screen_shard_spread_alpha * s.r2) * w8 * e * u);
  }
}

/**
 * A thin disc flash to R, then the shards fly out. Each shard moves out by
 * the room it has and shrinks to nothing on the rim, with a streak behind
 * it; the forward shards go first.
 */
function shatteredDiscSurge(c: DetonationCtx): void {
  const F = figs(c, 'shattered-disc');
  const R = reachOf(c), w8 = W(c), t = c.t;
  const Tf = F.front_sec + F.front_per_px * R, Tc = Tf + F.surge_crack_sec;
  const e = buffEnv(t, F.surge_in_sec, F.surge_out_sec, F.surge_end_sec);
  if (e <= 0) return;
  const fd = w8 * F.surge_flash_alpha * fade(t, Tc, Tc + 0.12);
  if (fd > 0) glow3(c, 0, 0, R * outExpo(t / Tf), 0, WHITE, fd, 0.7, FOAM, fd * 0.8, 1, c.ink, fd * 0.7);
  if (t < Tc * 0.6) return;
  const set = once(4, c, shardSetFor), lw = px(c, 1);
  for (let i = 0; i < set.shards.length; i++) {
    const s = set.shards[i], ang = s.d > 0.5 ? Math.atan2(s.cy, s.cx) : s.r1 * TAU, f = fwd(c, ang);
    const u = outCubic((t - Tc) / lerp(F.surge_slow_fly_sec, F.surge_fast_fly_sec, f));
    const mv = Math.max(0, R - s.d - 1) * u, sc = 1 - 0.9 * u;
    const mx = Math.cos(ang) * mv, my = Math.sin(ang) * mv, a = w8 * e * (1 - smooth(0.6, 1, u));
    if (a <= 0.004) continue;
    if (mv > 0.5) line(c, s.cx, s.cy, s.cx + mx, s.cy + my, FOAM, F.surge_streak_alpha * a, lw, R);
    placeShard(s, R, mx, my, sc);
    shape(c, set.keys[i], placed.x, placed.y, placed.s, c.ink, F.surge_shard_alpha * a);
    polyline(c, sxs, sys, placed.n, true, FOAM, F.surge_edge_alpha * a, lw);
  }
}

// --- card 05 ------------------------------------------------------------------

/** The masked returns stand one each this many px of R, and each band is
 *  this wide, or this share of R. */
const MASK_STEP_PX = 12;
const MASK_HALF_PX = 4;
const MASK_HALF_SHARE = 0.1;
/** The noise trace takes one point each this many px of the rim. */
const TRACE_STEP_PX = 2.5;

/**
 * The returns come back masked: wide, faint, soft bands and no crisp arc.
 * On the rim a noise trace jitters and then goes flat into a smooth line
 * over a soft band: the noise is hushed.
 */
function sonarReturnScreen(c: DetonationCtx): void {
  const F = figs(c, 'sonar-return');
  const R = reachOf(c), w8 = W(c), t = c.t, T = F.front_sec + F.front_per_px * R;
  const e = buffEnv(t, F.screen_in_sec, F.screen_out_sec, F.screen_end_sec);
  if (e <= 0) return;
  const rf = R * clamp01(t / T);
  if (t < T) ringA(c, rf, FOAM, F.screen_scan_alpha * w8, px(c, 1));
  // The masked returns: soft bands, no crisp arc.
  const m = Math.max(2, Math.round(R / MASK_STEP_PX)), hw = Math.min(MASK_HALF_PX, R * MASK_HALF_SHARE);
  for (let j = 1; j < m; j++) {
    const r = (R * j) / m, tj = (T * r) / R;
    if (t < tj) continue;
    const a = w8 * e * F.screen_band_alpha * (0.15 + 0.85 * Math.exp(-(t - tj) / F.screen_band_hold_sec));
    band(c, r - hw, r, c.ink, 0, a);
    band(c, r, r + hw, c.ink, a, 0);
  }
  if (t >= T) {
    // The noise trace on the rim goes flat.
    const ee = t - T, amp = Math.min(R * 0.15, 4) * Math.exp(-ee / F.screen_hush_sec);
    const n = Math.min(RIM_STEPS - 1, Math.max(48, Math.round((TAU * R) / TRACE_STEP_PX)));
    for (let q = 0; q <= n; q++) {
      const an = (q / n) * TAU, nz = 0.5 + 0.5 * Math.sin(an * 17 + ee * 25) * Math.sin(an * 7 - ee * 13);
      const rr = R - px(c, 1) - amp * nz;
      rxs[q] = Math.cos(an) * rr; rys[q] = Math.sin(an) * rr;
    }
    polyline(c, rxs, rys, n + 1, false, c.ink,
      w8 * e * (F.screen_trace_alpha + F.screen_trace_flash_alpha * Math.exp(-ee / F.screen_trace_fade_sec)), px(c, 1.3), R);
    band(c, R - Math.min(6, R * 0.2), R, FOAM, 0, F.screen_rim_alpha * w8 * e * smooth(0, 1, ee));
  }
  disc(c, 0, 0, Math.min(3, R * 0.2), FOAM, F.screen_dot_alpha * w8 * e);
}

/** The Doppler dashes: one ray each this many px of the rim, from this
 *  share of R. */
const DASH_RAY_PX = 9;
const DASH_FROM_SHARE = 0.22;

/**
 * The scan line runs out at twice the speed. Behind it, short Doppler
 * dashes run out on every ray and leave on the rim, more and faster on the
 * forward side. A small chevron at the centre points forward.
 */
function sonarReturnSurge(c: DetonationCtx): void {
  const F = figs(c, 'sonar-return');
  const R = reachOf(c), w8 = W(c), t = c.t, T = (F.front_sec + F.front_per_px * R) * F.surge_front_ratio;
  const e = buffEnv(t, F.surge_in_sec, F.surge_out_sec, F.surge_end_sec);
  if (e <= 0) return;
  const rf = R * clamp01(t / T);
  if (t < T) ringA(c, rf, STEEL, F.surge_scan_alpha * w8, px(c, 1.3));
  // The Doppler dashes: launched a gap apart on each ray, each runs out
  // and leaves on the rim.
  const nr = Math.max(12, Math.round((TAU * R) / DASH_RAY_PX)), r0 = R * DASH_FROM_SHARE, w = px(c, 1.2);
  for (let i = 0; i < nr; i++) {
    const an = (i / nr) * TAU, f = fwd(c, an), v = R * lerp(F.surge_slow_dash_ratio, F.surge_fast_dash_ratio, f);
    const L = Math.max(1.5, Math.min(6, R * 0.08)) * (0.7 + 0.8 * f);
    const ca = Math.cos(an), sa = Math.sin(an);
    for (let k = 0; k < F.surge_dash_count; k++) {
      const head = r0 + (t - T * 0.3 - k * F.surge_dash_gap_sec) * v;
      if (head < r0 || head > R + L) continue;
      const h = Math.min(head, R - px(c, 0.6)), tl = Math.max(r0, head - L);
      if (tl >= h) continue;
      line(c, ca * tl, sa * tl, ca * h, sa * h, k ? c.ink : FOAM, w8 * e * (F.surge_dash_alpha - 0.15 * k), w, R);
    }
  }
  // The forward chevron at the centre. A round with no course has no
  // forward, so it draws none.
  if (Number.isNaN(c.course)) return;
  const m = Math.min(4, R * 0.3), fx = Math.cos(c.course), fy = Math.sin(c.course), nx = -fy, ny = fx;
  sxs[0] = -fx * m * 0.4 + nx * m * 0.7; sys[0] = -fy * m * 0.4 + ny * m * 0.7;
  sxs[1] = fx * m * 0.6; sys[1] = fy * m * 0.6;
  sxs[2] = -fx * m * 0.4 - nx * m * 0.7; sys[2] = -fy * m * 0.4 - ny * m * 0.7;
  polyline(c, sxs, sys, 3, false, STEEL, F.surge_chevron_alpha * w8 * e, px(c, 1.2), R);
}

// --- card 07 ------------------------------------------------------------------

/** The quiet boil swings each notch at this rate a second. */
const QUIET_SWING_RATE = 3;
/** The inner quiet sheet stands at this share of the outer one. */
const QUIET_CORE_SHARE = 0.7;

/**
 * The quiet boil of card 07 at scale `s`, its notches calmed by `calm`:
 * the baked fill of the nearest calm step (`quietKey`) and the swung
 * stroke, both a hair inside R as the card draws them.
 */
function quietBoil(
  c: DetonationCtx, b: Boil, R: number, s: number, calm: number, t: number,
  fill: number, edge: number, w: number,
): void {
  const Rq = R - px(c, 0.7);
  if (fill > 0) shape(c, quietKey(R, c.seed, 1 - calm), 0, 0, (Rq / R) * s, FOAM, fill);
  if (edge <= 0) return;
  const n = Math.min(b.M, bxs.length);
  for (let k = 0; k < n; k++) {
    const th = (k / b.M) * TAU;
    const nk = b.n[k] * (1 - calm) * (BOIL_SWING_MID + BOIL_SWING * Math.sin(t * QUIET_SWING_RATE + k * BOIL_SWING_STEP));
    const r = Rq * (1 - b.amp * nk) * s;
    bxs[k] = Math.cos(th) * r; bys[k] = Math.sin(th) * r;
  }
  polyline(c, bxs, bys, n, true, FOAM, edge, w, R);
}

/**
 * A quiet boil. A thin foam sheet comes up slowly, and its ragged edge
 * calms to a smooth circle on R: the water goes still. The flecks drift a
 * little, stop and go soft.
 */
function depthChargeBoilScreen(c: DetonationCtx): void {
  const F = figs(c, 'depth-charge-boil');
  const R = reachOf(c), w8 = W(c), t = c.t, T = (F.front_sec + F.front_per_px * R) * F.screen_front_ratio;
  const e = buffEnv(t, F.screen_in_sec, F.screen_out_sec, F.screen_end_sec);
  if (e <= 0) return;
  const E = once(7, c, boilSetFor).boil;
  const s = outCubic(t / T), calm = smooth(T * 0.6, T + F.screen_calm_sec, t);
  if (s > 0) {
    quietBoil(c, E, R, s, calm, t, F.screen_fill_alpha * w8 * e,
      F.screen_edge_alpha * w8 * e * (1 - 0.4 * calm), px(c, 1.2));
    quietBoil(c, E, R, s * QUIET_CORE_SHARE, calm, t, F.screen_core_alpha * w8 * e, 0, 0);
  }
  // The flecks drift a little, stop and go soft.
  const fs = Math.max(0.5, Math.min(1.4, R / 60)), nf = Math.round(E.flecks.length * 0.6);
  for (let i = 0; i < nf; i++) {
    const f = E.flecks[i], age = t - (F.screen_fleck_from_sec + f.ts * F.screen_fleck_spread_sec);
    if (age < 0) continue;
    const d = f.d * boilEdge(E, R, f.a) * 0.9, drift = 2 * (1 - Math.exp(-age * 1.8));
    const x = Math.cos(f.a) * d + Math.sin(f.ph) * drift, y = Math.sin(f.a) * d + Math.cos(f.ph) * drift;
    const r = fs * (0.5 + f.s) * (1 + 0.8 * smooth(0, 2, age));
    if (!inside(x, y, r, R)) continue;
    disc(c, x, y, r, FOAM, F.screen_fleck_alpha * w8 * e * smooth(0, 0.2, age) * (1 - 0.6 * smooth(0.4, 2.5, age)));
  }
}

/** The bow wave spans this far each side of the course, in radians. */
const BOW_HALF_RAD = 0.9;

/**
 * The boil runs out fast as a thin ragged ring and is gone. Flecks race
 * out on rays with short tails and go out on the ragged rim, and a crescent
 * of bow wave lights the forward rim.
 */
function depthChargeBoilSurge(c: DetonationCtx): void {
  const F = figs(c, 'depth-charge-boil');
  const R = reachOf(c), w8 = W(c), t = c.t, T = (F.front_sec + F.front_per_px * R) * F.surge_front_ratio;
  const e = buffEnv(t, F.surge_in_sec, F.surge_out_sec, F.surge_end_sec);
  if (e <= 0) return;
  const set = once(7, c, boilSetFor), E = set.boil;
  const s = outCubic(t / T), ra = w8 * fade(t, T, T + F.surge_ring_fade_sec);
  if (ra > 0 && s > 0) {
    shape(c, set.key, 0, 0, s, FOAM, F.surge_fill_alpha * ra);
    raggedStroke(c, E, R, s, t, FOAM, F.surge_ring_alpha * ra, px(c, 1.3));
  }
  // The wake: flecks race out on rays and go out on the ragged rim.
  const fs = Math.max(0.5, Math.min(1.4, R / 60)), nf = Math.round(E.flecks.length * 0.6), lw = px(c, 1);
  for (let i = 0; i < nf; i++) {
    const f = E.flecks[i], ff = fwd(c, f.a), ts = F.surge_fleck_from_sec + f.ts * F.surge_fleck_spread_sec;
    const u = (t - ts) / lerp(F.surge_slow_fleck_sec, F.surge_fast_fleck_sec, ff);
    if (u < 0 || u > 1) continue;
    const edge = boilEdge(E, R, f.a) - 1, d0 = f.d * edge * 0.4, d = lerp(d0, edge, outCubic(u));
    const dt = Math.max(d0, d - (2 + 6 * ff));
    const al = F.surge_fleck_alpha * w8 * e * smooth(0, 0.1, u) * (1 - smooth(0.75, 1, u));
    const ca = Math.cos(f.a), sa = Math.sin(f.a), r = fs * (0.5 + f.s);
    line(c, ca * dt, sa * dt, ca * d, sa * d, FOAM, 0.6 * al, lw, R);
    if (inside(ca * d, sa * d, r, R)) disc(c, ca * d, sa * d, r, FOAM, al);
  }
  // The bow wave: a crescent of foam on the forward rim. A round with no
  // course has no forward rim, so it draws none.
  const bu = (t - F.surge_bow_from_sec) / F.surge_bow_sec;
  if (bu > 0 && bu < 1 && !Number.isNaN(c.course)) {
    arc(c, 0, 0, R * (1 - E.amp) - 1.5, c.course - BOW_HALF_RAD, c.course + BOW_HALF_RAD,
      FOAM, F.surge_bow_alpha * w8 * e * Math.sin(Math.PI * bu), px(c, 2.2));
  }
}

// --- card 08 ------------------------------------------------------------------

/** A settling hex comes in this much large. */
const HEX_SETTLE_SCALE = 1.14;
/** A hex within this many hex radii of R is a rim hex. */
const HEX_RIM_HEXES = 2.2;

/**
 * The hexes settle in at random, soft filled and with no outline, each
 * from a little large to its place. The ring of rim hexes takes a paler
 * foam fill: a hex curtain.
 */
function hexStampScreen(c: DetonationCtx): void {
  const F = figs(c, 'hex-stamp');
  const R = reachOf(c), w8 = W(c), t = c.t, T = (F.front_sec + F.front_per_px * R) * F.screen_front_ratio;
  const e = buffEnv(t, F.screen_in_sec, F.screen_out_sec, F.screen_end_sec);
  if (e <= 0) return;
  const H = once(8, c, hexesFor), hs = H.h * HEX_INSET;
  for (let i = 0; i < H.list.length; i++) {
    const h = H.list[i], td = T * ((0.6 * h.d) / R + 0.4 * h.j), u = smooth(td, td + F.screen_settle_sec, t);
    if (u <= 0) continue;
    const rim = h.d > R - HEX_RIM_HEXES * H.h;
    hexFill(c, h.x, h.y, hs * lerp(HEX_SETTLE_SCALE, 1, u), rim ? FOAM : c.ink,
      (rim ? F.screen_rim_alpha : F.screen_hex_alpha) * u * w8 * e);
  }
}

/** The three waves of the surge, each weaker. */
const HEX_WAVES = [1, 0.7, 0.45] as const;

/**
 * A fast wave runs out three times, each weaker. Each hex it passes lights
 * a chevron that points away from the centre, so the whole disc shows the
 * outward flow; the forward hexes light first.
 */
function hexStampSurge(c: DetonationCtx): void {
  const F = figs(c, 'hex-stamp');
  const R = reachOf(c), w8 = W(c), t = c.t, T = (F.front_sec + F.front_per_px * R) * F.surge_front_ratio;
  const e = buffEnv(t, F.surge_in_sec, F.surge_out_sec, F.surge_end_sec);
  if (e <= 0) return;
  const H = once(8, c, hexesFor), hs = H.h * HEX_INSET, free = Number.isNaN(c.course), w = px(c, 1.2);
  for (let i = 0; i < H.list.length; i++) {
    const h = H.list[i], centre = h.d <= 0.5;
    // The centre hex points forward; with no course it lights no chevron.
    const ux = centre ? (free ? 1 : Math.cos(c.course)) : h.x / h.d;
    const uy = centre ? (free ? 0 : Math.sin(c.course)) : h.y / h.d;
    const f = fwd(c, Math.atan2(uy, ux)), nx = -uy, ny = ux, m = hs * 0.6;
    let lit = 0;
    for (let q = 0; q < 3; q++) {
      const ee = t - q * F.surge_wave_gap_sec - ((T * h.d) / R) * lerp(1.25, 0.75, f);
      if (ee >= 0) lit = Math.max(lit, fade(ee, 0, F.surge_lit_sec) * HEX_WAVES[q]);
    }
    const a = w8 * e * lit;
    if (a <= 0.01) continue;
    if (lit > 0.5) hexFill(c, h.x, h.y, hs, c.ink, F.surge_fill_alpha * a);
    if (centre && free) continue;
    sxs[0] = h.x - ux * m * 0.45 + nx * m * 0.7; sys[0] = h.y - uy * m * 0.45 + ny * m * 0.7;
    sxs[1] = h.x + ux * m * 0.6; sys[1] = h.y + uy * m * 0.6;
    sxs[2] = h.x - ux * m * 0.45 - nx * m * 0.7; sys[2] = h.y - uy * m * 0.45 - ny * m * 0.7;
    polyline(c, sxs, sys, 3, false, lit > 0.7 ? WHITE : c.ink, a, w);
  }
}

/** The built looks of this file, by id. */
export const RECIPES: Readonly<Record<string, DetonationRecipe>> = {
  'fireball-rim': withBuffForms(fireballRim, fireballRimScreen, fireballRimSurge),
  'pressure-shell': withBuffForms(pressureShell, pressureShellScreen, pressureShellSurge),
  'cavitation-collapse': withBuffForms(cavitationCollapse, cavitationCollapseScreen, cavitationCollapseSurge),
  'shattered-disc': withBuffForms(shatteredDisc, shatteredDiscScreen, shatteredDiscSurge),
  'sonar-return': withBuffForms(sonarReturn, sonarReturnScreen, sonarReturnSurge),
  'depth-charge-boil': withBuffForms(depthChargeBoil, depthChargeBoilScreen, depthChargeBoilSurge),
  'hex-stamp': withBuffForms(hexStamp, hexStampScreen, hexStampSurge),
};

/** The looks of this file still to build. They draw the AUTO look. */
export const PENDING: readonly string[] = [];
